import { useState } from "react";
import { toast } from "sonner";
import { cn } from "@/utils/cn";
import { pruneGrouped, visibleGroups } from "../groupedOptions";
import { PrimaryButton } from "./visuals";

/**
 * Step 4, "Responder perguntas": one question at a time, answers as chips,
 * the answered ones as a short summary above. The questions fill the base
 * skill's params; nothing here calls a model. Writing freely ("Ou escreva do
 * seu jeito") goes to the setup assistant, which fills the same answers.
 */

const optionsOf = (field) => (Array.isArray(field?.options) ? field.options : []);
const labelsOf = (field, values) =>
  (values || []).map((v) => optionsOf(field).find((o) => o.value === v)?.label || v).join(", ");

export const QUESTIONS = [
  {
    id: "areas",
    label: "Áreas",
    title: "Quais áreas o escritório atende?",
    done: (v) => (v.areas || []).length > 0,
    summary: (v, f) => labelsOf(f.areas, v.areas),
  },
  {
    id: "niches",
    label: "Casos",
    title: "Quais casos vocês pegam?",
    hint: "Sem marcar nenhum, o agente atende todos os casos da área.",
    done: () => true,
    summary: (v, f) => ((v.niches || []).length ? labelsOf(f.niches, v.niches) : "Todos"),
  },
  {
    id: "tone",
    label: "Tom",
    title: "Como o agente deve falar?",
    done: (v) => Boolean(v.tone),
    summary: (v, f) => labelsOf(f.tone, [v.tone]),
  },
  {
    id: "hours",
    label: "Horário",
    title: "Quando a equipe atende?",
    done: (v) => (v.serviceDays || []).length > 0 && v.serviceStart && v.serviceEnd && v.serviceStart < v.serviceEnd,
    summary: (v, f) => `${labelsOf(f.serviceDays, v.serviceDays)}, ${v.serviceStart} às ${v.serviceEnd}`,
  },
  {
    id: "name",
    label: "Nome",
    title: "Como o agente se apresenta?",
    hint: "Pode deixar em branco.",
    done: () => true,
    summary: (v) => v.assistantName || "Sem nome",
  },
];

function Chip({ selected, children, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "min-h-[44px] rounded-full border px-4 text-sm font-medium transition-colors",
        selected
          ? "border-foreground bg-foreground text-background"
          : "border-black/[.1] bg-white text-foreground hover:border-foreground/40 dark:border-white/15 dark:bg-transparent",
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

function MultiChips({ options, value, onChange, label }) {
  const selected = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(options.map((o) => o.value).filter((x) => next.has(x)));
  };
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Chip key={o.value} selected={selected.has(o.value)} onClick={() => toggle(o.value)}>
          {o.label}
        </Chip>
      ))}
    </div>
  );
}

function QuestionBody({ question, fields, values, set }) {
  switch (question.id) {
    case "areas":
      return <MultiChips label="Áreas" options={optionsOf(fields.areas)} value={values.areas} onChange={(v) => set("areas", v)} />;
    case "niches": {
      const groups = visibleGroups(optionsOf(fields.niches), values.areas);
      return (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.group}>
              {groups.length > 1 && <p className="mb-2 text-[13px] font-medium text-muted-foreground">{g.label}</p>}
              <MultiChips
                label={g.label}
                options={g.options}
                value={(values.niches || []).filter((n) => g.options.some((o) => o.value === n))}
                onChange={(picked) => {
                  const others = (values.niches || []).filter((n) => !g.options.some((o) => o.value === n));
                  set("niches", [...others, ...picked]);
                }}
              />
            </div>
          ))}
        </div>
      );
    }
    case "tone":
      return (
        <div role="radiogroup" aria-label="Tom" className="flex flex-wrap gap-2">
          {optionsOf(fields.tone).map((o) => (
            <Chip key={o.value} role="radio" aria-checked={values.tone === o.value} selected={values.tone === o.value} onClick={() => set("tone", o.value)}>
              {o.label}
            </Chip>
          ))}
        </div>
      );
    case "hours":
      return (
        <div className="space-y-4">
          <MultiChips label="Dias" options={optionsOf(fields.serviceDays)} value={values.serviceDays} onChange={(v) => set("serviceDays", v)} />
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <label className="flex items-center gap-2">
              Das
              <input
                type="time"
                value={values.serviceStart || ""}
                onChange={(e) => set("serviceStart", e.target.value)}
                className="min-h-[44px] rounded-xl border border-black/[.1] bg-white px-3 text-foreground dark:border-white/15 dark:bg-transparent"
              />
            </label>
            <label className="flex items-center gap-2">
              às
              <input
                type="time"
                value={values.serviceEnd || ""}
                onChange={(e) => set("serviceEnd", e.target.value)}
                className="min-h-[44px] rounded-xl border border-black/[.1] bg-white px-3 text-foreground dark:border-white/15 dark:bg-transparent"
              />
            </label>
          </div>
        </div>
      );
    case "name":
      return (
        <input
          aria-label="Nome do agente"
          value={values.assistantName || ""}
          maxLength={fields.assistantName?.max || 40}
          placeholder="Ex.: Ana"
          onChange={(e) => set("assistantName", e.target.value)}
          className="min-h-[48px] w-full max-w-xs rounded-full border border-black/[.1] bg-white px-4 text-[15px] text-foreground dark:border-white/15 dark:bg-transparent"
        />
      );
    default:
      return null;
  }
}

/**
 * The question shown (`index`) lives in the page, so the answers and the place
 * survive the build screen and an error coming back from it.
 */
export default function QuestionsMode({ baseSkill, values, onChange, onFinish, finishing, onAsk, index, onIndex: setIndex }) {
  const fields = Object.fromEntries((baseSkill?.fields || []).map((f) => [f.name, f]));
  const [text, setText] = useState("");
  const [asking, setAsking] = useState(false);
  const [reply, setReply] = useState(null);

  const question = QUESTIONS[index];
  const last = index === QUESTIONS.length - 1;
  const set = (name, value) => onChange(pruneGrouped(baseSkill?.fields, { ...values, [name]: value }, name));

  const ask = async (e) => {
    e.preventDefault();
    const message = text.trim();
    if (!message || asking) return;
    setAsking(true);
    try {
      setReply(await onAsk(message));
      setText("");
    } catch (err) {
      toast.error(err.message || "Não foi possível entender agora. Tente pelas opções.");
    } finally {
      setAsking(false);
    }
  };

  return (
    <section aria-label="Perguntas" className="overflow-hidden rounded-[20px] border border-black/[.08] bg-white dark:border-white/10 dark:bg-white/[.03]">
      {index > 0 && (
        <dl className="border-b border-black/[.05] px-6 py-1.5 dark:border-white/[.06]">
          {QUESTIONS.slice(0, index).map((q, i) => (
            <div key={q.id} className="flex items-center gap-3 py-2.5">
              <dt className="w-32 shrink-0 text-sm text-muted-foreground">{q.label}</dt>
              <dd className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground">{q.summary(values, fields)}</dd>
              <button type="button" onClick={() => setIndex(i)} className="min-h-[36px] text-sm text-muted-foreground underline-offset-4 hover:underline">
                Editar
              </button>
            </div>
          ))}
        </dl>
      )}

      <div className="p-6">
        <p className="mb-1 text-[13px] text-muted-foreground">
          {index + 1} de {QUESTIONS.length}
        </p>
        <h2 className="mb-1.5 text-xl font-semibold text-foreground">{question.title}</h2>
        {question.hint && <p className="mb-4 text-sm text-muted-foreground">{question.hint}</p>}
        <div className={cn(!question.hint && "mt-4")}>
          <QuestionBody question={question} fields={fields} values={values} set={set} />
        </div>
        {reply && <p className="mt-4 rounded-2xl bg-violet-50 px-4 py-3 text-sm text-violet-900 dark:bg-violet-500/10 dark:text-violet-200">{reply}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 px-6 pb-6">
        {onAsk ? (
          <form onSubmit={ask} className="flex min-w-[14rem] flex-1">
            <label htmlFor="agent-free-text" className="sr-only">
              Escrever do seu jeito
            </label>
            <input
              id="agent-free-text"
              value={text}
              disabled={asking}
              onChange={(e) => setText(e.target.value)}
              placeholder={asking ? "Lendo..." : "Ou escreva do seu jeito"}
              className="min-h-[48px] w-full rounded-full border border-black/[.1] bg-black/[.015] px-4 text-[15px] text-foreground dark:border-white/15 dark:bg-transparent"
            />
          </form>
        ) : (
          <span className="flex-1" />
        )}
        {last ? (
          <PrimaryButton onClick={onFinish} disabled={finishing || !QUESTIONS.every((q) => q.done(values))}>
            {finishing ? "Criando..." : "Criar agentes"}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={() => setIndex(index + 1)} disabled={!question.done(values)}>
            Próxima
          </PrimaryButton>
        )}
      </div>
    </section>
  );
}
